import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { EVENT_TYPE_OPTIONS } from "../../shared/event-types";
import { formatMoney } from "../../shared/money";
import { PhoneError, parsePhone, sanitizePhoneInput } from "../../shared/phone";
import type { DaySlot } from "../../shared/tasting";
import { api } from "../api";
import { Banner, messageOf, onSubmit } from "./ui";

type TurnstileApi = {
  render: (element: HTMLElement, options: { sitekey: string }) => string;
  getResponse: (widgetId?: string) => string;
  reset: (widgetId?: string) => void;
};

function turnstileApi(): TurnstileApi | null {
  return (window as Window & { turnstile?: TurnstileApi }).turnstile ?? null;
}

type PublicConfig = {
  business_name: string;
  turnstile: string;
  turnstile_site_key: string | null;
  venues: { id: string; name: string }[];
};

export function InquirePage() {
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);
  const [slot, setSlot] = useState<DaySlot | null>(null);
  const widgetId = useRef<string | null>(null);
  const widgetHost = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api<PublicConfig>("/public/config")
      .then((result) => setConfig({ ...result, venues: result.venues ?? [] }))
      .catch((reason: unknown) => setError(messageOf(reason)));
  }, []);

  useEffect(() => {
    const siteKey = config?.turnstile_site_key;
    const host = widgetHost.current;
    if (!siteKey || !host) return;
    const render = () => {
      const apiClient = turnstileApi();
      if (!apiClient || widgetId.current) return;
      widgetId.current = apiClient.render(host, { sitekey: siteKey });
    };
    if (turnstileApi()) {
      render();
      return;
    }
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.onload = render;
    document.head.appendChild(script);
  }, [config?.turnstile_site_key]);

  return (
    <div className="public">
      <p className="meta"><a href="/login">Σύνδεση προσωπικού</a></p>
      <h1>Αίτημα εκδήλωσης</h1>
      <p className="lede">Πρώτη φάση για το {config?.business_name ?? "στούντιο"}. Γράψε την ημερομηνία, τα άτομα, το κτήμα και το είδος. Η αποστολή καταγράφει την επιθυμία. Δεν κρατά την ημερομηνία και δεν είναι προσφορά.</p>
      <Banner error={error} note={receipt} />
      <form
        className="panel"
        onSubmit={onSubmit(async (body) => {
          setError(null);
          try {
            const key = crypto.randomUUID();
            const turnstileToken = turnstileApi()?.getResponse(widgetId.current ?? undefined) ?? "";
            const people = body.adults.trim();
            if (!/^\d+$/.test(people) || Number(people) < 1) {
              setError("Τα άτομα γράφονται ως ακέραιος αριθμός.");
              return;
            }
            let phone: string | null = null;
            try {
              phone = parsePhone(body.phone);
            } catch (reason) {
              if (reason instanceof PhoneError) {
                setError(reason.message);
                return;
              }
              throw reason;
            }
            const result = await api<{ message: string; receipt_id: string }>("/public/inquiries", {
              method: "POST",
              headers: { "idempotency-key": key },
              body: JSON.stringify({
                given_name: body.given_name,
                family_name: body.family_name,
                email: body.email,
                phone,
                event_type: body.event_type,
                start_date: body.start_date,
                slot,
                adults: Number(people),
                venue_id: body.venue_id,
                notes: body.notes,
                turnstile_token: turnstileToken,
              }),
            });
            turnstileApi()?.reset(widgetId.current ?? undefined);
            setReceipt(`${result.message} Απόδειξη ${result.receipt_id}.`);
          } catch (reason) {
            turnstileApi()?.reset(widgetId.current ?? undefined);
            setError(messageOf(reason));
          }
        })}
      >
        <div className="fields">
          <label>Ονομα<input name="given_name" required /></label>
          <label>Επωνυμο<input name="family_name" required /></label>
          <label>Email<input name="email" type="email" required /></label>
          <label>Τηλέφωνο<input name="phone" type="tel" inputMode="tel" onInput={(event) => { const next = sanitizePhoneInput(event.currentTarget.value); if (next !== event.currentTarget.value) event.currentTarget.value = next; }} /></label>
          <label>Εκδήλωση
            <select name="event_type" required defaultValue="">
              <option value="" disabled>Διάλεξε είδος</option>
              {EVENT_TYPE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label>Ατομα<input name="adults" type="number" inputMode="numeric" min={1} step={1} required onInput={(event) => { const digits = event.currentTarget.value.replace(/\D/g, ""); if (digits !== event.currentTarget.value) event.currentTarget.value = digits; }} /></label>
          <label>Ημερομηνία<input name="start_date" type="date" required /></label>
          <div className="inquire-slot" role="group" aria-label="Ώρα εκδήλωσης">
            <span>Ώρα <small>(προαιρετικό)</small></span>
            <div className="choice-row">
              <button type="button" className="choice" aria-pressed={slot === "midday"} onClick={() => setSlot(slot === "midday" ? null : "midday")}><span>μεσημέρι</span></button>
              <button type="button" className="choice" aria-pressed={slot === "evening"} onClick={() => setSlot(slot === "evening" ? null : "evening")}><span>βράδυ</span></button>
            </div>
          </div>
          <label>Περιοχή
            <select name="venue_id" required defaultValue="" disabled={!config}>
              <option value="" disabled>Διάλεξε κτήμα</option>
              {(config?.venues ?? []).map((venue) => <option key={venue.id} value={venue.id}>{venue.name}</option>)}
            </select>
          </label>
        </div>
        <label>Τι πρέπει να ξέρουμε;<textarea name="notes" /></label>
        <div ref={widgetHost} />
        <button type="submit">Αποστολή αιτήματος</button>
      </form>
    </div>
  );
}

type PublicProposal = {
  number: string;
  status: string;
  html: string;
  subject_to_confirmation: boolean;
  options: { key: string; label: string; gross_minor: number }[];
};

export function ClientProposalPage() {
  const { token = "" } = useParams();
  const [proposal, setProposal] = useState<PublicProposal | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    api<PublicProposal>(`/public/proposals/${token}`)
      .then(setProposal)
      .catch((reason: unknown) => setError(messageOf(reason)));
  }, [token]);

  async function respond(action: "accept" | "changes", body: Record<string, string>) {
    setError(null);
    try {
      const result = await api<{ reserved: boolean }>(`/public/proposals/${token}/${action === "accept" ? "accept" : "changes"}`, {
        method: "POST",
        body: JSON.stringify({ signer_name: body.signer_name, option_key: body.option_key || "selected", comment: body.comment }),
      });
      setNote(action === "accept" ? `Καταγράφηκε. Ο χώρος ${result.reserved ? "κρατήθηκε" : "δεν κρατήθηκε"}. Το προσωπικό πρέπει ακόμη να επιβεβαιώσει την κράτηση.` : "Το αίτημα αλλαγής στάλθηκε στο στούντιο.");
    } catch (reason) {
      setError(messageOf(reason));
    }
  }

  return (
    <div className="public">
      <h1>{proposal?.number ?? "Πρόταση"}</h1>
      <p className="lede">{proposal?.subject_to_confirmation ? "Αυτή η προσφορά υπόκειται σε επιβεβαίωση του χώρου." : "Διάβασε την πρόταση από κάτω."} Η αποδοχή της δεν κρατά την ημερομηνία.</p>
      <Banner error={error} note={note} />
      {!proposal && !error ? <p>Φόρτωση πρότασης…</p> : null}
      {proposal ? (
        <>
          <iframe className="proposal-frame" title="Πρόταση πελάτη" srcDoc={proposal.html} />
          <p><a href={`/public/proposals/${token}/pdf`}>Λήψη PDF</a></p>
          <div className="split">
            <form className="panel" onSubmit={onSubmit(async (body) => respond("accept", body))}>
              <h2>Αποδοχή αυτής της έκδοσης</h2>
              <label>Το όνομά σου<input name="signer_name" required /></label>
              <label>Επιλογή
                <select name="option_key">
                  {proposal.options.map((option) => <option key={option.key} value={option.key}>{option.label} · {formatMoney(option.gross_minor)} μικτά</option>)}
                </select>
              </label>
              <button type="submit">Αποδοχή, χωρίς κράτηση</button>
            </form>
            <form className="panel" onSubmit={onSubmit(async (body) => respond("changes", body))}>
              <h2>Αίτημα αλλαγής</h2>
              <label>Το όνομά σου<input name="signer_name" required /></label>
              <label>Τι να αλλάξει;<textarea name="comment" required /></label>
              <button className="secondary" type="submit">Αίτημα αλλαγών</button>
            </form>
          </div>
        </>
      ) : null}
    </div>
  );
}
